'use client'

import React, { memo } from 'react'

import { useRouter } from 'next/navigation'

import {
  Analytics,
  ArrowForward,
  Fingerprint,
  Hub,
  Security,
  VerifiedUser,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Fade,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'

import { AUTH_ROUTES } from '@/lib/routes/auth-routes'

interface SystemModulesProps {
  mounted: boolean
}

const systemModules = [
  {
    title: 'Enrollment Hub',
    subtitle: 'Citizen registration & biometric capture',
    description:
      'Advanced biometric enrollment system with facial recognition, fingerprint scanning, and document verification.',
    icon: <Fingerprint />,
    stats: { daily: '8,432', total: '12.4M', accuracy: '99.97%' },
    features: ['Biometric Capture', 'Document Scan', 'Data Validation', 'Quality Check'],
    color: '#1E3A5F',
    accessible: true,
    href: AUTH_ROUTES.STAFF.LOGIN,
  },
  {
    title: 'Identity Verification',
    subtitle: 'Real-time authentication services',
    description:
      'State-of-the-art identity verification with AI-powered liveness detection and multi-factor authentication.',
    icon: <VerifiedUser />,
    stats: { daily: '145,780', latency: '47ms', success: '99.94%' },
    features: ['Face Match', 'Liveness Check', '2FA/MFA', 'Risk Score'],
    color: '#059669',
    accessible: false,
    href: '/verification',
  },
  {
    title: 'Analytics Engine',
    subtitle: 'Comprehensive system insights',
    description:
      'Real-time analytics dashboard with predictive modeling, anomaly detection, and performance metrics.',
    icon: <Analytics />,
    stats: { reports: '2,847', queries: '4.2M/day', insights: '847' },
    features: ['Real-time Data', 'Predictive Analytics', 'Custom Reports', 'API Analytics'],
    color: '#F59E0B',
    accessible: false,
    href: '/analytics',
  },
  {
    title: 'Security Operations',
    subtitle: '24/7 threat monitoring & response',
    description:
      'Enterprise security operations center with automated threat detection and incident response.',
    icon: <Security />,
    stats: { threats: '0', scans: '24/7', incidents: '3' },
    features: ['Threat Detection', 'SIEM Integration', 'Incident Response', 'Compliance'],
    color: '#DC2626',
    accessible: false,
    href: '/security',
  },
]

const SystemModules = memo<SystemModulesProps>(({ mounted }) => {
  const theme = useTheme()
  const router = useRouter()

  const handleModuleClick = (href: string, accessible: boolean) => {
    if (accessible) {
      router.push(href)
    }
  }

  return (
    <Box sx={{ py: 8, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
      <Container maxWidth="xl">
        <Box textAlign="center" mb={6}>
          <Chip
            icon={<Hub />}
            label="SYSTEM MODULES"
            color="primary"
            variant="outlined"
            sx={{ mb: 3, fontWeight: 600 }}
          />
          <Typography variant="h3" fontWeight={800} gutterBottom>
            Core System Components
          </Typography>
          <Typography variant="h6" color="text.secondary" maxWidth={800} mx="auto">
            Access and manage all aspects of the national digital identity infrastructure
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {systemModules.map((module, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
              <Fade in={mounted} timeout={800 + index * 100}>
                <Card
                  elevation={0}
                  onClick={() => handleModuleClick(module.href, module.accessible)}
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    cursor: module.accessible ? 'pointer' : 'default',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': module.accessible
                      ? {
                          transform: 'translateY(-4px)',
                          boxShadow: theme.shadows[8],
                          borderColor: alpha(module.color, 0.3),
                          '&::before': {
                            opacity: 1,
                          },
                        }
                      : {},
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, ${module.color} 0%, ${alpha(module.color, 0.6)} 100%)`,
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={3}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          bgcolor: alpha(module.color, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: module.color,
                          fontSize: 32,
                        }}
                      >
                        {module.icon}
                      </Box>

                      <Box>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                          {module.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {module.subtitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {module.description}
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        {Object.entries(module.stats).map(([key, value]) => (
                          <Grid key={key} size={4}>
                            <Box textAlign="center">
                              <Typography variant="subtitle1" fontWeight={700}>
                                {value}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {key}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {module.features.map((feature) => (
                          <Chip
                            key={feature}
                            label={feature}
                            size="small"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              bgcolor: alpha(module.color, 0.1),
                              color: module.color,
                            }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ px: 3, pb: 3 }}>
                    {module.accessible ? (
                      <Button
                        fullWidth
                        variant="contained"
                        endIcon={<ArrowForward />}
                        sx={{
                          bgcolor: module.color,
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: alpha(module.color, 0.9),
                            '& .MuiButton-endIcon': {
                              transform: 'translateX(4px)',
                            },
                          },
                        }}
                      >
                        Access Module
                      </Button>
                    ) : (
                      <Button fullWidth variant="outlined" disabled sx={{ fontWeight: 600 }}>
                        Restricted Access
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
})

SystemModules.displayName = 'SystemModules'

export default SystemModules
